import { useNavigate } from "@solidjs/router";
import { createEffect, createSignal, For, onCleanup, Show, type JSX } from "solid-js";
import { CHOICES, KINDS, isComingSoon, type KindId, type Part } from "../report";
import { Button } from "./Button";
import { comingSoon } from "./ComingSoon";
import { Sheet } from "./Sheet";

/**
 * Figma R1 / R4 overlay: 45 % dim over the whole screen (app bar included), a tap on it or Escape
 * closes, the bottom sheet on top. In the sheet, the handle's margin collapses into the first
 * heading's: that heading's top margin is the whole gap under the handle.
 */
function ReportOverlay(props: { label: string; onClose: () => void; children: JSX.Element }) {
  let box!: HTMLDivElement;
  createEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") props.onClose();
    };
    document.addEventListener("keydown", onKey);
    queueMicrotask(() => box.focus());
    onCleanup(() => document.removeEventListener("keydown", onKey));
  });
  return (
    <div
      ref={(el) => (box = el)}
      class="fixed inset-0 z-40 mx-auto max-w-[430px] outline-none"
      role="dialog"
      aria-modal="true"
      aria-label={props.label}
      tabindex="-1"
    >
      <button
        type="button"
        class="absolute inset-0 bg-black/45"
        aria-label="Fermer"
        onClick={() => props.onClose()}
      />
      <Sheet onClose={() => props.onClose()}>{props.children}</Sheet>
    </div>
  );
}

/**
 * Figma R1 "Qu'est-ce qui n'allait pas ?" (116:14550), over 08 or the H3 receipt: the bike option
 * opens the report flow (`href`, see `reportHref`), the other one is not designed yet (« coming
 * soon »). The host screen mounts <ComingSoonToast/>.
 */
export function ReportChoiceSheet(props: { open: boolean; onClose: () => void; href: string }) {
  const navigate = useNavigate();
  const pick = (id: (typeof CHOICES)[number]["id"]) => {
    if (isComingSoon(id)) return comingSoon();
    props.onClose();
    navigate(props.href);
  };
  return (
    <Show when={props.open}>
      <ReportOverlay label="Qu'est-ce qui n'allait pas ?" onClose={() => props.onClose()}>
        <h2 class="mt-[42px] text-center text-[22px] font-semibold leading-[1.25] tracking-[0.22px] text-strong">
          Qu'est-ce qui n'allait pas ?
        </h2>
        <p class="mt-[6px] text-center text-sm leading-[1.25] tracking-[0.14px] text-text">
          On vous oriente au bon endroit.
        </p>
        <div class="-mx-[5px] mt-[22px] mb-[78px] flex flex-col gap-[10px]">
          <For each={CHOICES}>
            {(c) => (
              <button
                type="button"
                class="flex h-14 w-full items-center justify-between rounded-[12px] bg-surface2 pl-4 pr-3 text-left active:brightness-95"
                onClick={() => pick(c.id)}
              >
                <span class="text-[15px] font-medium leading-[1.25] tracking-[0.15px] text-strong">
                  {c.label}
                </span>
                <img src="/icons/chevron-right.svg" width="24" height="24" alt="" />
              </button>
            )}
          </For>
        </div>
      </ReportOverlay>
    </Show>
  );
}

/**
 * Figma R4 "Dites-nous en plus :" (116:14851), over R3: the part pill and the kind of problem.
 * « Confirmer » needs a kind; a part already reported opens with its kind selected.
 */
export function ReportPartSheet(props: {
  part: Part;
  kind: KindId | undefined;
  onClose: () => void;
  onConfirm: (kind: KindId) => void;
}) {
  // Initial value only: the host mounts one sheet per opened part.
  const [kind, setKind] = createSignal<KindId | undefined>(props.kind);
  return (
    <ReportOverlay label="Dites-nous en plus" onClose={() => props.onClose()}>
      <h2 class="mt-5 text-lg font-semibold leading-[1.25] tracking-[0.18px] text-strong">
        Dites-nous en plus :
      </h2>
      <span class="mt-3 inline-flex h-[30px] items-center rounded-[15px] border-[1.5px] border-violet bg-surface2 px-[14.5px] text-sm font-medium leading-[1.25] tracking-[0.14px] text-violet">
        {props.part.label}
      </span>
      <div class="mt-[18px] flex flex-col" role="radiogroup" aria-label="Type de problème">
        <For each={KINDS}>
          {(k) => (
            <label class="flex h-11 cursor-pointer items-start gap-3">
              <input
                type="radio"
                name="report-kind"
                class="peer sr-only"
                checked={kind() === k.id}
                onChange={() => setKind(k.id)}
              />
              <span
                class="relative h-[22px] w-[22px] shrink-0 rounded-full peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-violet"
                aria-hidden="true"
              >
                <Show
                  when={kind() === k.id}
                  fallback={<img src="/icons/radio-off.svg" width="22" height="22" alt="" />}
                >
                  <img src="/icons/radio-on-ring.svg" width="22" height="22" alt="" />
                  <img
                    src="/icons/radio-on-dot.svg"
                    width="10"
                    height="10"
                    alt=""
                    class="absolute left-[6px] top-[6px]"
                  />
                </Show>
              </span>
              <span class="pt-[2px] text-[15px] leading-[1.25] tracking-[0.15px] text-strong">
                {k.label}
              </span>
            </label>
          )}
        </For>
      </div>
      <div class="-mx-[5px] mt-[108px] mb-[26px] short:mt-6">
        <Button
          disabled={!kind()}
          onClick={() => {
            const k = kind();
            if (k) props.onConfirm(k);
          }}
        >
          Confirmer
        </Button>
      </div>
    </ReportOverlay>
  );
}
